import { IamSlideupConfig, parseIamMessageData, resolveIamWebUrl } from 'apps/auth/analytics/brazeIamMessageData';
import { BR_VISIT } from 'apps/auth/constants/braze/brazeEvents';
import { BRAZE_IAM_JSON_KEY, BRAZE_IAM_PLACEMENT_ID_KEY } from 'apps/auth/constants/braze/brazeIam';
import { BR_COUNTRY, BR_LANGUAGE, BR_TIME, BR_WEB_NAME, BR_WEB_URL } from 'apps/auth/constants/braze/brazeParams';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import Cookies from 'universal-cookie';

type BrazeBanner = { isControl?: boolean; [key: string]: unknown } | null | undefined;
type BrazeBannersMap = Record<string, BrazeBanner>;
type BrazeContentCardsEvent = { cards?: unknown[] };
type BrazeContentCardsCallback = (event: BrazeContentCardsEvent) => void;
type BrazeBannersCallback = (banners: BrazeBannersMap) => void;
type BrazeCustomEventPayload = Record<string, unknown>;
type BrazeBannerUpdateListener = (placementIds: string[]) => void;
type BrazeReadyListener = () => void;
type BrazeIamNavigateHandler = (path: string) => void;

type BrazeInAppMessageButton = {
    uri?: string;
    subscribeToClickedEvent?: (callback: () => unknown) => string;
};
type BrazeInAppMessage = {
    extras?: Record<string, string>;
    uri?: string;
    buttons?: BrazeInAppMessageButton[];
    subscribeToClickedEvent?: (callback: () => unknown) => string;
};
type BrazeInAppMessageCallback = (message: BrazeInAppMessage) => void;
type BrazeIamSlideupHandlers = { onPrimaryClick: () => void };
type BrazeIamSlideupRenderer = (config: IamSlideupConfig, handlers: BrazeIamSlideupHandlers) => void;

interface BrazeUser {
    setCustomUserAttribute: (key: string, value: unknown, merge?: boolean) => void;
}

interface BrazeSDK {
    initialize: (
        apiKey?: string,
        config?: {
            baseUrl?: string;
            allowUserSuppliedJavascript?: boolean;
            enableLogging?: boolean;
            minimumIntervalBetweenTriggerActionsInSeconds?: number;
        },
    ) => Promise<unknown> | void;
    changeUser: (userId: string) => void;
    openSession: () => void;
    logCustomEvent: (eventName: string, params?: BrazeCustomEventPayload) => void;
    requestBannersRefresh?: (placements: string[], onSuccess?: () => void) => void;
    subscribeToBannersUpdates?: (callback: BrazeBannersCallback) => void;
    getBanner?: (placementId: string) => BrazeBanner;
    insertBanner?: (banner: BrazeBanner, container: HTMLElement) => void;
    isInitialized?: () => boolean;
    subscribeToContentCardsUpdates?: (callback: BrazeContentCardsCallback) => void;
    requestContentCardsRefresh?: () => void;
    getUser: () => BrazeUser;
    disableSDK?: () => void;
    subscribeToInAppMessage?: (callback: BrazeInAppMessageCallback) => string;
    showInAppMessage?: (message: BrazeInAppMessage, container?: HTMLElement) => void;
    automaticallyShowInAppMessages?: () => void;
    removeSubscription?: (subscriptionId: string) => void;
    logInAppMessageImpression?: (message: BrazeInAppMessage) => void;
    logInAppMessageClick?: (message: BrazeInAppMessage) => void;
    InAppMessage?: { ClickAction?: { NONE?: unknown } };
}

declare const braze: BrazeSDK | undefined;

const cookies = new Cookies();

let brazeInitPromise: Promise<unknown> | null = null;
let brazeIsInitialized = false;
let brazeBannerSubscriptionInitialized = false;
let brazeBannerRefreshRequested = false;
let brazeCurrentUserId: string | null = null;
let brazeIamSubscriptionInitialized = false;
let currentIamPlacement: string | null = null;
let brazeIamNavigateHandler: BrazeIamNavigateHandler | null = null;
let brazeIamSlideupRenderer: BrazeIamSlideupRenderer | null = null;

const TRY_COUNTER = __isDev__ ? 0 : 5;

const sdkEndpoint = process.env.BRAZE_SDK_ENDPOINT;
const apiKey = process.env.BRAZE_API_KEY;

export const BRAZE_BANNER_PLACEMENTS = {
    MAIN_SCREEN: 'main_screen_top',
    DELIVERY_OPTIONS: 'delivery_options_screen',
    PAYMENT_OPTIONS: 'payment_options_screen',
    PROFILE: 'profile_screen',
} as const;

export const BRAZE_BANNER_PLACEMENT_IDS = Object.values(BRAZE_BANNER_PLACEMENTS);

export const PLACEMENT_ID = BRAZE_BANNER_PLACEMENTS.MAIN_SCREEN;

export const brazeScript = '/braze.min.js?v=6.3.1';

const brazeBannerUpdateListeners = new Set<BrazeBannerUpdateListener>();
const brazeReadyListeners = new Set<BrazeReadyListener>();

export const subscribeToBrazeBannerUpdates = (cb: BrazeBannerUpdateListener): (() => void) => {
    brazeBannerUpdateListeners.add(cb);
    return () => {
        brazeBannerUpdateListeners.delete(cb);
    };
};

export const emitBrazeBannerUpdates = (placementIds: string[]): void => {
    brazeBannerUpdateListeners.forEach((cb) => cb(placementIds));
};

export const isBrazeSdkReady = (): boolean => Boolean(brazeIsInitialized && typeof braze !== 'undefined' && braze.isInitialized?.());

export const subscribeToBrazeReady = (cb: BrazeReadyListener): (() => void) => {
    if (isBrazeSdkReady()) {
        cb();
        return () => {};
    }
    brazeReadyListeners.add(cb);
    return () => {
        brazeReadyListeners.delete(cb);
    };
};

export const emitBrazeReady = (): void => {
    brazeReadyListeners.forEach((cb) => cb());
};

export const setCurrentIamPlacement = (placementId: string | null): void => {
    currentIamPlacement = placementId;
};

export const getCurrentIamPlacement = (): string | null => currentIamPlacement;

// Registered once from the app root so IAM clicks to internal destinations use
// client-side routing instead of a full page reload.
export const setBrazeIamNavigateHandler = (handler: BrazeIamNavigateHandler | null): void => {
    brazeIamNavigateHandler = handler;
};

export const setBrazeIamSlideupRenderer = (renderer: BrazeIamSlideupRenderer | null): void => {
    brazeIamSlideupRenderer = renderer;
};

const navigateIamWebUrl = (webUrl: string | null): void => {
    const resolution = resolveIamWebUrl(webUrl);
    if (resolution.kind === 'route' && resolution.target && brazeIamNavigateHandler) {
        brazeIamNavigateHandler(resolution.target);
    } else if (resolution.kind === 'external' && resolution.target && typeof window !== 'undefined') {
        window.open(resolution.target, '_blank', 'noopener');
    }
};

const getIamNoneClickAction = (): unknown => (typeof braze !== 'undefined' ? braze.InAppMessage?.ClickAction?.NONE : undefined);

// Same-origin URLs become a relative path for the SPA router; external URLs return null
// so Braze keeps its default navigation.
const toInternalIamPath = (uri?: string): string | null => {
    if (!uri || typeof window === 'undefined') {
        return null;
    }
    try {
        const url = new URL(uri, window.location.origin);
        if (url.origin !== window.location.origin) {
            return null;
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
};

const handleIamClick = (uri?: string): unknown => {
    const path = toInternalIamPath(uri);
    if (path && brazeIamNavigateHandler) {
        brazeIamNavigateHandler(path);
        // Suppress Braze's own full-page navigation now that we've routed client-side.
        return getIamNoneClickAction();
    }
    // External URL, or no handler registered: let Braze navigate as usual.
    return undefined;
};

const registerIamClickHandlers = (message: BrazeInAppMessage): void => {
    try {
        message.subscribeToClickedEvent?.(() => handleIamClick(message.uri));
        message.buttons?.forEach((button) => {
            button.subscribeToClickedEvent?.(() => handleIamClick(button.uri));
        });
    } catch (e) {
        if (__isDev__) {
            logWarn('[Braze] IAM click handler registration failed', e);
        }
    }
};

const subscribeToBrazeIam = (): void => {
    if (brazeIamSubscriptionInitialized || typeof braze === 'undefined' || !braze.subscribeToInAppMessage) {
        return;
    }
    braze.subscribeToInAppMessage((message) => {
        const placementId = message?.extras?.[BRAZE_IAM_PLACEMENT_ID_KEY];
        if (!placementId || placementId !== currentIamPlacement) {
            return;
        }
        const slideupConfig = parseIamMessageData(message?.extras?.[BRAZE_IAM_JSON_KEY]);
        if (slideupConfig && brazeIamSlideupRenderer) {
            braze?.logInAppMessageImpression?.(message);
            brazeIamSlideupRenderer(slideupConfig, {
                onPrimaryClick: () => {
                    braze?.logInAppMessageClick?.(message);
                    navigateIamWebUrl(slideupConfig.webUrl);
                },
            });
            return;
        }
        registerIamClickHandlers(message);
        braze?.showInAppMessage?.(message);
    });
    brazeIamSubscriptionInitialized = true;
};

// Set the current screen placement, then log the triggering event with the
// properties. Placement is set first so the incoming IAM matches the screen synchronously.
export const trackBrazeIamScreenShown = (eventName: string, placementId: string, properties: BrazeCustomEventPayload = {}): void => {
    setCurrentIamPlacement(placementId);
    const cleanProperties = Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined && value !== null && value !== ''));

    const sendTriggerEvent = (): void => {
        sendBrazeEvent(eventName, cleanProperties);
    };
    if (isBrazeSdkReady()) {
        sendTriggerEvent();
        return;
    }
    const unsubscribe = subscribeToBrazeReady(() => {
        sendTriggerEvent();
        unsubscribe?.();
    });
};

export const getBrazeBannerCacheKey = (placementId: string): string => `brazeBannerCache_${placementId}`;

export const getBrazeBannerHideKey = (bannerId: string): string => `brazeBannerHide_${bannerId}`;

export const getCachedBrazeBanner = (placementId: string): BrazeBanner => {
    if (!__isBrowser__ || typeof sessionStorage === 'undefined') {
        return null;
    }
    const cachedBanner = sessionStorage.getItem(getBrazeBannerCacheKey(placementId));
    if (!cachedBanner) {
        return null;
    }
    try {
        return JSON.parse(cachedBanner) as BrazeBanner;
    } catch (error) {
        logWarn('Failed to parse cached Braze banner payload', error);
        return null;
    }
};

const writeBannerToCache = (placementId: string, banner: BrazeBanner): void => {
    if (typeof sessionStorage === 'undefined') {
        return;
    }
    if (!banner || banner.isControl) {
        sessionStorage.removeItem(getBrazeBannerCacheKey(placementId));
    } else {
        sessionStorage.setItem(getBrazeBannerCacheKey(placementId), JSON.stringify(banner));
    }
};

export const brazeInit = (): Promise<unknown> | null => {
    if (!__isBrowser__ || typeof document === 'undefined') {
        return null;
    }

    if (!brazeInitPromise) {
        brazeInitPromise = new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = brazeScript;
            script.async = true;
            script.onload = () => {
                if (!apiKey) {
                    resolve(null);
                    return;
                }

                const initResult = braze?.initialize(apiKey, {
                    baseUrl: sdkEndpoint,
                    allowUserSuppliedJavascript: true,
                    // enable for debugging in dev mode:
                    // enableLogging: true,
                });

                void Promise.resolve(initResult).then((res) => {
                    if (res) {
                        subscribeToBrazeIam();
                        brazeIsInitialized = true;
                        emitBrazeReady();
                    }
                    resolve(res ?? null);
                });
            };
            document.head.appendChild(script);
        });
    }

    return brazeInitPromise;
};

const checkBrazeInit = (): Promise<unknown> | null => brazeInitPromise;

export const brazeChangeUser = (ballId?: string): void => {
    if (!ballId) {
        return;
    }

    let attempts = 0;
    if (typeof braze !== 'undefined') {
        pushBrazeChangeUser(ballId);
        return;
    }

    const timerId = setInterval(() => {
        attempts += 1;
        if (typeof braze !== 'undefined') {
            pushBrazeChangeUser(ballId);
            clearInterval(timerId);
            return;
        }
        if (attempts >= TRY_COUNTER) {
            clearInterval(timerId);
            if (!__isDev__) {
                logWarn('[brazeChangeUser] Braze did not initialize in time');
            }
        }
    }, 700);
};

const pushBrazeChangeUser = (ballId: string): void => {
    void checkBrazeInit()?.then(() => {
        if (!brazeIsInitialized || typeof braze === 'undefined') {
            return;
        }
        if (brazeCurrentUserId !== ballId) {
            braze.changeUser(ballId);
            brazeCurrentUserId = ballId;
            brazeBannerRefreshRequested = false;
        }
        if (braze.subscribeToBannersUpdates) {
            if (!brazeBannerSubscriptionInitialized) {
                braze.subscribeToBannersUpdates((banners = {}) => {
                    const receivedIds = Object.keys(banners).filter((id) => (BRAZE_BANNER_PLACEMENT_IDS as readonly string[]).includes(id));
                    receivedIds.forEach((placementId) => writeBannerToCache(placementId, banners[placementId]));
                    if (receivedIds.length > 0) {
                        emitBrazeBannerUpdates(receivedIds);
                    }
                });
                brazeBannerSubscriptionInitialized = true;
            }
        }
        subscribeToBrazeIam();
        braze.openSession();
        if (!brazeBannerRefreshRequested && braze.requestBannersRefresh) {
            brazeBannerRefreshRequested = true;
            braze.requestBannersRefresh(BRAZE_BANNER_PLACEMENT_IDS, () => {
                BRAZE_BANNER_PLACEMENT_IDS.forEach((placementId) => writeBannerToCache(placementId, braze.getBanner?.(placementId)));
                emitBrazeBannerUpdates(BRAZE_BANNER_PLACEMENT_IDS);
            });
        }
        sessionStorage?.setItem('isLoggedIn', 'true');
    });
};

export const sendBrazeEvent = (eventName: string, params: BrazeCustomEventPayload = {}): void => {
    let timerId: ReturnType<typeof setInterval> | null = null;
    let counter = 1;

    if (typeof braze !== 'undefined') {
        pushBrazeEvent(eventName, params);
    } else {
        timerId = setInterval(() => {
            if (counter >= TRY_COUNTER) {
                if (timerId) {
                    clearInterval(timerId);
                }
                return;
            }
            pushBrazeEvent(eventName, params);
            counter += 1;
        }, 700);
    }
};

const pushBrazeEvent = (eventName: string, params: BrazeCustomEventPayload): void => {
    void checkBrazeInit()?.then(() => {
        if (!brazeIsInitialized || typeof braze === 'undefined') {
            return;
        }
        braze.logCustomEvent(eventName, params);
    });
};

type BrazePgaApp = { country?: string };

export const brazePgaViewEvent = (url: string, pageTitle: string, app: BrazePgaApp = {}, _auth?: unknown, lang?: string): void => {
    let timerId: ReturnType<typeof setInterval> | null = null;
    let counter = 1;
    if (typeof braze !== 'undefined') {
        pushBrazePgaViewEvent(url, pageTitle, app, _auth, lang);
    } else {
        timerId = setInterval(() => {
            if (counter >= TRY_COUNTER) {
                if (timerId) {
                    clearInterval(timerId);
                }
                return;
            }
            pushBrazePgaViewEvent(url, pageTitle, app, _auth, lang);
            counter += 1;
        }, 700);
    }
};

const pushBrazePgaViewEvent = (url: string, pageTitle: string, app: BrazePgaApp, _auth?: unknown, lang?: string): void => {
    const params: BrazeCustomEventPayload = {
        [BR_WEB_URL]: url,
        [BR_WEB_NAME]: pageTitle,
        [BR_LANGUAGE]: lang,
        [BR_COUNTRY]: app.country,
        [BR_TIME]: new Date().toString(),
    };

    void checkBrazeInit()?.then(() => {
        if (!brazeIsInitialized || typeof braze === 'undefined') {
            return;
        }
        if (!cookies.get(BR_VISIT)) {
            const date = new Date();
            date.setDate(date.getDate() + 60 * 24 * 365);
            cookies.set(BR_VISIT, 1, { path: '/', secure: true, expires: date });
            braze.logCustomEvent(BR_VISIT, params);
        }
    });
};

export const fetchContentCards = (ballId: string, onCardsFetched?: (cards: unknown[]) => void): (() => void) | undefined => {
    if (!ballId || typeof window === 'undefined') {
        return undefined;
    }
    let attempts = 0;

    const intervalId = setInterval(() => {
        void checkBrazeInit()?.then(() => {
            if (!brazeIsInitialized || typeof braze === 'undefined') {
                return;
            }
            clearInterval(intervalId);
            braze.openSession();
            braze.changeUser(ballId);
            braze.subscribeToContentCardsUpdates?.((event) => {
                const cards = event.cards || [];
                onCardsFetched?.(cards);
            });
            braze.requestContentCardsRefresh?.();
        });
        attempts += 1;
        if (attempts >= TRY_COUNTER) {
            clearInterval(intervalId);
            if (!__isDev__) {
                logWarn('[fetchContentCards] Braze did not initialize in time');
            }
        }
    }, 300);

    return () => clearInterval(intervalId);
};

export const appendToUserObjectArray = (key: string, obj: Record<string, unknown>): void => {
    let timerId: ReturnType<typeof setInterval> | null = null;
    let counter = 1;
    if (typeof braze !== 'undefined') {
        pushAppendToUserObjectArray(key, obj);
    } else {
        timerId = setInterval(() => {
            if (counter >= TRY_COUNTER) {
                if (timerId) {
                    clearInterval(timerId);
                }
                return;
            }
            pushAppendToUserObjectArray(key, obj);
            counter += 1;
        }, 700);
    }
};

const pushAppendToUserObjectArray = (key: string, obj: Record<string, unknown>): void => {
    void checkBrazeInit()?.then(() => {
        if (!brazeIsInitialized || typeof braze === 'undefined') {
            return;
        }
        try {
            const payload = { $add: [obj] };
            braze.getUser().setCustomUserAttribute(key, payload, true);
        } catch (e) {
            if (__isDev__) {
                logWarn('[Braze] appendToUserObjectArray failed', key, obj, e);
            }
        }
    });
};

export const brazeDisable = (): void => {
    if (!__isBrowser__ || typeof window === 'undefined') {
        return;
    }
    if (typeof braze !== 'undefined') {
        try {
            braze.disableSDK?.();
        } catch (e) {
            if (__isDev__) {
                logWarn('[Braze] disableSDK failed', e);
            }
        }
    }
    brazeBannerSubscriptionInitialized = false;
    brazeBannerRefreshRequested = false;
    brazeCurrentUserId = null;
    brazeIamSubscriptionInitialized = false;
    currentIamPlacement = null;
};
